import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { Node } from '@tiptap/core';
import { DOMSerializer, Fragment, DOMParser as PMDOMParser, type Schema } from '@tiptap/pm/model';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { ZyDEInstructionNodeView } from './zyde-instruction-node-view';
import ZyDENodeView from './zyde-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        zyDE: {
            insertZyDE: (options?: Partial<ZyDEAttributes>) => ReturnType;
        };
    }
}

export interface ZyDEProgramFile {
    program: string;
    filename: string;
    main: boolean;
    data: boolean;
}

export interface ZyDEAttributes {
    id?: string | null;
    language?: string;
    caption?: string | null;
    inputEnabled?: boolean;
    resetEnabled?: boolean;
    stacked?: boolean;
    activityType?: string | null;

    // zyInput and zyOutputFiles attributes
    inputText?: string;
    outputFiles?: string;

    multifiles?: ZyDEProgramFile[];

    // For seeding the main file when calling `insertZyDE`
    program?: string;
}

export function defaultExtension(language: string): string {
    const m: Record<string, string> = {
        cpp: 'cpp',
        c: 'c',
        java: 'java',
        python: 'py',
        python3: 'py',
        r: 'R',
        sql: 'sql',
        verilog: 'v',
        web: 'html',
        arm: 's',
        mips: 's',
        riscv: 's',
        coral: 'coral',
    };
    return m[language] || 'txt';
}

export function defaultMainFilename(language: string): string {
    if (language === 'java') {
        return 'Main.java';
    }
    const ext = defaultExtension(language);
    return `main.${ext}`;
}

export const ZYDE_LANGUAGE_OPTIONS = [
    { value: 'cpp', label: 'C++' },
    { value: 'c', label: 'C' },
    { value: 'java', label: 'Java' },
    { value: 'python', label: 'Python' },
    { value: 'python3', label: 'Python 3' },
    { value: 'r', label: 'R' },
    { value: 'arm', label: 'ARM' },
    { value: 'mips', label: 'MIPS' },
    { value: 'riscv', label: 'RISC-V' },
    { value: 'coral', label: 'Coral' },
    { value: 'web', label: 'Web' },
    { value: 'verilog', label: 'Verilog' },
    { value: 'sql', label: 'SQL' },
];

export const ZyDEInstructionHeadless = Node.create({
    name: 'zyDEInstruction',

    content() {
        return getBasicContentSchema();
    },

    group: 'block',

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('id'),
                renderHTML: attributes => {
                    if (!attributes.id) {
                        return {};
                    }
                    return { id: attributes.id };
                },
            },
        };
    },

    parseHTML() {
        return [{ tag: 'zyDEInstruction' }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['zyDEInstruction', HTMLAttributes, 0];
    },
});

export const ZyDENodeHeadless = Node.create({
    name: 'zyDE',

    content: 'zyDEInstruction',

    group: 'block',

    isolating: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
            },
            language: {
                default: 'cpp',
            },
            caption: {
                default: null,
            },
            inputEnabled: {
                default: false,
            },
            resetEnabled: {
                default: false,
            },
            stacked: {
                default: false,
            },
            activityType: {
                default: null,
            },
            inputText: {
                default: '',
            },
            outputFiles: {
                default: '',
            },
            multifiles: {
                default: [],
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'zyDE',
                getAttrs: element => {
                    if (!(element instanceof HTMLElement)) {
                        return false;
                    }
                    const inputEl = element.querySelector('zyInput');
                    const outputEl = element.querySelector('zyOutputFiles');
                    const multifiles = parseZyDEPrograms(element);

                    return {
                        id: element.getAttribute('id'),
                        language: element.getAttribute('language')?.toLowerCase() || 'cpp',
                        caption: element.getAttribute('caption') || null,
                        inputEnabled: element.getAttribute('input') === 'true',
                        resetEnabled: element.getAttribute('reset') === 'true',
                        stacked: element.getAttribute('stacked') === 'true',
                        activityType: element.getAttribute('activity_type') || null,
                        inputText: inputEl ? textFromElement(inputEl) : '',
                        outputFiles: outputEl ? textFromElement(outputEl).trim() : '',
                        multifiles,
                    };
                },
                getContent: (node, schema) => {
                    if (!(node instanceof HTMLElement)) {
                        const zyDEInstruction = schema.nodes.zyDEInstruction;
                        const paragraph = schema.nodes.paragraph;
                        return Fragment.from(zyDEInstruction.create({}, Fragment.from(paragraph.create())));
                    }
                    return zyDEInstructionFragmentFromDom(node, schema);
                },
            },
        ];
    },

    renderHTML({ node }) {
        const { attrs } = node;
        const rootAttrs: Record<string, string> = {};
        if (attrs.id) {
            rootAttrs.id = String(attrs.id);
        }
        if (attrs.language) {
            rootAttrs.language = String(attrs.language);
        }
        if (attrs.caption) {
            rootAttrs.caption = String(attrs.caption);
        }
        if (attrs.inputEnabled) {
            rootAttrs.input = 'true';
        }
        if (attrs.resetEnabled) {
            rootAttrs.reset = 'true';
        }
        if (attrs.stacked) {
            rootAttrs.stacked = 'true';
        }
        if (attrs.activityType) {
            rootAttrs.activity_type = String(attrs.activityType);
        }

        const multifiles = Array.isArray(attrs.multifiles) ? (attrs.multifiles as ZyDEProgramFile[]) : [];

        const dom = document.createElement('zyDE');
        for (const [name, value] of Object.entries(rootAttrs)) {
            dom.setAttribute(name, value);
        }

        const serializer = DOMSerializer.fromSchema(node.type.schema);
        serializer.serializeFragment(node.content, { document }, dom);

        if (attrs.inputText) {
            const inputEl = document.createElement('zyInput');
            inputEl.textContent = String(attrs.inputText);
            dom.appendChild(inputEl);
        }

        for (const file of multifiles) {
            const progEl = document.createElement('zyProgram');
            if (file.filename) {
                progEl.setAttribute('filename', file.filename);
            }
            if (file.main) {
                progEl.setAttribute('main', 'true');
            }
            if (file.data) {
                progEl.setAttribute('data', 'true');
            }
            progEl.textContent = file.program ?? '';
            dom.appendChild(progEl);
        }

        if (attrs.outputFiles) {
            const outEl = document.createElement('zyOutputFiles');
            outEl.textContent = String(attrs.outputFiles);
            dom.appendChild(outEl);
        }

        return { dom };
    },

    addCommands() {
        return {
            insertZyDE:
                (options: Partial<ZyDEAttributes> = {}) =>
                ({ commands }) => {
                    const language = options.language || 'cpp';
                    return commands.insertContent({
                        type: this.name,
                        attrs: {
                            language,
                            caption: options.caption ?? null,
                            inputEnabled: options.inputEnabled ?? false,
                            resetEnabled: options.resetEnabled ?? false,
                            stacked: options.stacked ?? false,
                            activityType: options.activityType ?? null,
                            inputText: options.inputText || '',
                            outputFiles: options.outputFiles || '',
                            multifiles: options.multifiles?.length
                                ? options.multifiles
                                : [
                                      {
                                          program: options.program ?? '',
                                          filename: defaultMainFilename(language),
                                          main: true,
                                          data: false,
                                      },
                                  ],
                        },
                        content: [
                            {
                                type: 'zyDEInstruction',
                                content: [{ type: 'paragraph' }],
                            },
                        ],
                    });
                },
        };
    },
});

function textFromElement(el: Element | null | undefined): string {
    return el?.textContent ?? '';
}

function parseZyDEPrograms(root: HTMLElement): ZyDEProgramFile[] {
    return Array.from(root.querySelectorAll('zyProgram')).map(p => ({
        program: textFromElement(p),
        filename: p.getAttribute('filename') || '',
        main: p.getAttribute('main') === 'true',
        data: p.getAttribute('data') === 'true',
    }));
}

/**
 * The parse rule uses `getContent` to supply content manually: parse **only** the inside
 * of `<zyDEInstruction>` into blocks, wrap that in one `zyDEInstruction` node, and return it as
 * the fragment. Legacy or minimal markup without `<zyDEInstruction>` (or with an empty one) gets
 * a single empty paragraph so the required child always exists.
 */
function zyDEInstructionFragmentFromDom(element: HTMLElement, schema: Schema): Fragment {
    const zyDEInstruction = schema.nodes.zyDEInstruction;
    const paragraph = schema.nodes.paragraph;
    const instrEl = element.querySelector('zyDEInstruction');

    if (!instrEl || instrEl.childNodes.length === 0) {
        return Fragment.from(zyDEInstruction.create({}, Fragment.from(paragraph.create())));
    }

    const slice = PMDOMParser.fromSchema(schema).parseSlice(instrEl, { preserveWhitespace: 'full' });
    return Fragment.from(zyDEInstruction.create({}, slice.content));
}

export const ZyDENode = ZyDENodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyDENodeView);
    },
});

export const ZyDEInstruction = ZyDEInstructionHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(ZyDEInstructionNodeView);
    },
});
